/**
 * Groups on screen: their circles placed around their nodes, their names, finding one under
 * the pointer, and moving one with everything inside it. The geometry itself (enclosing
 * circles, forces between groups) is in `../groups.ts`.
 */
import { layoutGroups } from "../groups";
import type { Scene } from "./index";
import type { SceneGroup, ScreenCircle } from "./types";
import { flat } from "./viewport";
import { GROUP_LABEL_MIN_R, GROUP_PAD, NAMES_ALWAYS, RIM_GRAB_PX } from "./constants";

/** What the scene does with groups. */
export interface SceneGroups {
  /** Places the group circles around their nodes as they are now (call before drawing). Sizes are in world units, so they do not depend on the zoom. */
  layoutGroups(): void;
  /** The group whose rim or label is under a screen point (the innermost one). */
  hitGroup(px: number, py: number): SceneGroup | null;
  /** The group whose name is under a screen point. Names are drawn above nodes, so they win. */
  hitGroupLabel(px: number, py: number): SceneGroup | null;
  /**
   * Whether a group's name is drawn (and can be clicked): always among a few groups; among
   * many, when its circle is big enough on screen to tell which group it names (zoomed out on
   * hundreds of groups, names would cover each other and everything else).
   */
  groupLabelShown(g: SceneGroup): boolean;
  /** The box of a group's name on screen, at the top of its circle. */
  groupLabel(g: SceneGroup): { x: number; y: number; w: number; h: number };
  /** A group's circle on screen (in 3D, the outline of its sphere). */
  groupCircle(g: SceneGroup): ScreenCircle;
  /** Moves every node of a group by a world offset. */
  moveGroup(g: SceneGroup, dx: number, dy: number, dz?: number): void;
}

/** The groups of `scene`. */
export function groupsOnScreen(scene: Scene): SceneGroups {
  return {
    layoutGroups() {
      if (!scene.groups.length) return;
      // World sizes, not screen sizes: the layout must not change when the view zooms.
      layoutGroups(scene.groups, GROUP_PAD, (n) => n.r * 1.3);
      if (!scene.showGroups) for (const g of scene.groups) g.visible = false;
    },

    hitGroup(px, py) {
      let best: SceneGroup | null = null;
      for (const g of scene.groups) {
        if (!g.visible || (best && best.depth >= g.depth)) continue;
        const c = scene.groupCircle(g);
        if (Math.abs(Math.hypot(px - c.x, py - c.y) - c.r) <= RIM_GRAB_PX) best = g;
      }
      return scene.hitGroupLabel(px, py) ?? best;
    },

    hitGroupLabel(px, py) {
      let best: SceneGroup | null = null;
      for (const g of scene.groups) {
        if (!g.visible || (best && best.depth >= g.depth) || !scene.groupLabelShown(g)) continue;
        const box = scene.groupLabel(g);
        if (px >= box.x && px <= box.x + box.w && py >= box.y && py <= box.y + box.h) best = g;
      }
      return best;
    },

    groupLabelShown: (g) => scene.groups.length <= NAMES_ALWAYS || scene.groupCircle(g).r >= GROUP_LABEL_MIN_R,

    groupLabel(g) {
      const w = g.group.name.length * 6.6 + 16;
      const h = 17;
      const c = scene.groupCircle(g);
      return { x: c.x - w / 2, y: c.y - c.r - h / 2, w, h };
    },

    groupCircle(g) {
      const f = flat(scene, g);
      const k = scene.view.k;
      return { x: f.x * k + scene.view.x, y: f.y * k + scene.view.y, r: g.r * f.scale * k };
    },

    moveGroup(g, dx, dy, dz = 0) {
      for (const n of g.all) Object.assign(n, { x: n.x + dx, y: n.y + dy, z: n.z + dz, vx: 0, vy: 0, vz: 0 });
      scene.layoutGroups();
    },
  };
}
